<template>
  <div>
    <cl-table-wrapper class-name="tw-bg-primary-grey-25 tw-px-0">
      <template #before>
        <div class="tw-flex tw-items-center tw-justify-between tw-pb-2">
          <div>
            <label
              for="msp-licence-usage-customers-table-search"
              class="tw-sr-only"
              >Search
            </label>
            <div class="tw-w-[320px]">
              <cl-input-search
                id="msp-licence-usage-customers-table-search"
                :name="'msp-licence-usage-customers-table-search'"
                test-id="msp-licence-usage-customers-table-search"
                :placeholder="t('search')"
                :size="'medium'"
                :value="filterState"
                :readonly="loading"
                @update:value="debounceSearch"
              />
            </div>
          </div>
          <div
            class="tw-col-span-6 max-md:tw-col-span-12 tw-flex tw-items-center tw-justify-end"
          >
            <div class="tw-inline-flex">
              <cl-table-column-visibility-toggle
                class="tw-relative"
                data-test-id="columns-visibility-button"
              >
                <div
                  v-for="column in columnsToggleList"
                  :key="column.id"
                  class="tw-mb-2"
                >
                  <label class="tw-flex tw-items-center tw-gap-2">
                    <cl-input-checkbox
                      :id="column.id"
                      :checked="column.getIsVisible()"
                      :disabled="
                        column.getIsVisible() &&
                        table.getVisibleLeafColumns().length <= 1
                      "
                      :indeterminate="false"
                      :name="column.id"
                      @change="toggleColumnVisibility(column)"
                    />
                    {{ column.columnDef.header }}
                  </label>
                </div>
              </cl-table-column-visibility-toggle>
            </div>
          </div>
        </div>
      </template>
      <template #table>
        <cl-table>
          <cl-thead>
            <cl-tr
              v-for="headerGroup in table.getHeaderGroups()"
              :key="headerGroup.id"
              :table-target="'thead'"
            >
              <cl-th
                v-for="header in headerGroup.headers"
                :key="header.id"
                :col-span="header.colSpan"
                :class="{
                  'tw-select-none': header.column.getCanSort(),
                  'tw-sticky tw-right-0 tw-bg-[inherit] tw-ml-auto':
                    header.column.getIsPinned(),
                }"
                scope="col"
              >
                <div class="tw-flex tw-h-7 tw-items-center tw-justify-between">
                  <button
                    class="tw-flex tw-items-center tw-h-7"
                    :class="{
                      'tw-text-primary-grey-300': loading,
                    }"
                    :data-test-id="`msp-licence-usage-customers-table-header-${header.id}`"
                    :disabled="loading"
                    @click="header.column.getToggleSortingHandler()?.($event)"
                  >
                    <template v-if="!header.isPlaceholder">
                      <FlexRender
                        :render="header.column.columnDef.header"
                        :props="header.getContext()"
                      />
                      <cl-th-sorting
                        v-if="header.column.getCanSort()"
                        :sort-direction="header.column.getIsSorted()"
                      />
                    </template>
                  </button>
                </div>
              </cl-th>
            </cl-tr>
          </cl-thead>
          <cl-tbody v-if="loading">
            <cl-tr-skeleton :rows="skeletonArray" />
          </cl-tbody>
          <cl-tbody v-else-if="!allAzureUsers.length">
            <cl-tr zebra>
              <cl-td
                colspan="100%"
                :class-name="'tw-text-center tw-pl-0 tw-pr-0 tw-pb-0'"
              >
                <t-empty>
                  <template #image>
                    <component :is="emptyState.icon" />
                  </template>
                  <template #title>
                    {{ emptyState.title }}
                  </template>
                  <template #description>
                    {{ emptyState.description }}
                  </template>
                </t-empty>
              </cl-td>
            </cl-tr>
          </cl-tbody>
          <cl-tbody v-else data-clarity-mask="true">
            <cl-tr
              v-for="(row, index) in table.getRowModel().rows"
              :key="row.id"
              :zebra="index % 2 === 0"
            >
              <cl-td
                v-for="cell in row.getVisibleCells()"
                :key="cell.id"
                :class="{
                  'tw-sticky tw-right-0 tw-bg-[inherit] tw-ml-auto':
                    cell.column.getIsPinned(),
                }"
              >
                <FlexRender
                  :render="cell.column.columnDef.cell"
                  :props="cell.getContext()"
                />
              </cl-td>
            </cl-tr>
          </cl-tbody>
        </cl-table>
      </template>
      <template #after>
        <div class="tw-relative">
          <div v-if="loading"><cl-skeleton-row /></div>
          <div v-else class="tw-flex tw-justify-between tw-items-baseline">
            <cl-table-page-size
              :placeholder="t('rows-per-page')"
              :page-size="table.getState().pagination.pageSize"
              :page-sizes="[10, 25, 50]"
              :handle-page-size-change="
                ($e: Event) =>
                  handlePageSizeChange(
                    Number(($e.target as HTMLInputElement).value),
                  )
              "
            />
            <cl-table-pagination-state
              :start="rowsIndexPointer.startIndex"
              :end="rowsIndexPointer.endIndex"
              :total="total"
              :placeholder="t('of')"
            />
            <cl-table-basic-pagination
              :previous-page-text="$t('previous-pagination')"
              :next-page-text="$t('next')"
              :can-previous-page="table.getCanPreviousPage()"
              :previous-page="table.previousPage"
              :can-next-page="table.getCanNextPage()"
              :next-page="table.nextPage"
            />
          </div>
        </div>
      </template>
    </cl-table-wrapper>
  </div>
</template>

<script setup lang="ts">
import { useUsersStore } from "@/store/users.store";

import TEmpty from "@/components/TEmpty";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { watch } from "vue";
import { useRoute } from "vue-router";
import { useGlobalOrganisationIDBasedOnRoute } from "@/composables/useGlobalOrganisationIDBasedOnRoute";
import {
  useRowsIndexPointer,
  useSkeletonArray,
  useServerSidePagination,
  useMemoDefaultParams,
  useMemoColumnVisibility,
  useColumnVisibility,
} from "@/composables/table-helpers";
import { useDebounceFn } from "@vueuse/core";
import {
  useVueTable,
  getCoreRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  FlexRender,
  ColumnDef,
} from "@tanstack/vue-table";
import { transformAntispoofUsersToUsers } from "@/modules/Usage/views/Customer/Overview/CustomersTable/utils/transformAntispoofUsersToUsers.ts";
import { DATA_TABLE_COOKIE } from "@/constants";
import {
  Customer,
  AntispoofUser,
} from "@/modules/Usage/views/Customer/Overview/CustomersTable/types";

const { globalOrganisationId } = useGlobalOrganisationIDBasedOnRoute();
const loading = ref(true);
const usersStore = useUsersStore();
const route = useRoute();

const getUsers = computed(() => usersStore.antispoofUsers as AntispoofUser[]);
const total = computed(() => usersStore.antispoofUsersTotal);

const allAzureUsers = computed(() =>
  transformAntispoofUsersToUsers(getUsers.value),
);

const fetchUsers = async () => {
  loading.value = true;
  clearCache();
  try {
    await usersStore.fetchAntispoofUsers({
      uuid: route.params.id || globalOrganisationId.value,
      params: {
        page: paramsState.page,
        results: paramsState.results,
        sortDir: paramsState.sortDir.toLocaleUpperCase() || "ASC",
        sortKey: paramsState.sortKey || "name",
        filter: paramsState.filter || "",
      },
    });
  } catch (e) {
    console.warn(e); // eslint-disable-line no-console
  } finally {
    loading.value = false;
  }
};

const clearCache = async () => {
  try {
    usersStore.clearUsersCache();
  } catch (e) {
    console.warn(e); // eslint-disable-line no-console
  }
};

const { t } = useI18n();

const columns: ColumnDef<Customer>[] = [
  {
    header: t("name"),
    accessorKey: "name",
  },
  {
    header: t("email-title"),
    accessorKey: "email",
  },
];

const cookieKey = ref<string>(
  DATA_TABLE_COOKIE.CUSTOMER_USAGE_PROTECTED_MAILBOXES_LIST,
);

const { defaultParams, handleParamsChange } = useMemoDefaultParams(
  cookieKey.value,
  {
    pageSize: 10,
    sortDir: "asc",
    sortKey: "name",
    filter: "",
  },
);
const { columnVisibility } = useMemoColumnVisibility(
  `${cookieKey.value}-columns`,
);

const {
  paramsState,
  filterState,
  sortingState,
  paginationState,
  handlePageSizeChange,
} = useServerSidePagination({
  pageIndex: 0,
  pageSize: defaultParams.value.pageSize,
  sortDir: defaultParams.value.sortDir,
  sortKey: defaultParams.value.sortKey,
  filter: "",
});

const emptyState = computed(() =>
  paramsState.filter
    ? {
        icon: "cl-icon-empty-folder",
        title: t("no-search-results-default"),
        description: t("subtext-no-search-results-default"),
      }
    : {
        icon: "cl-icon-empty-box",
        title: t("no-search-results-default"),
        description: t("no-customers"),
      },
);

const table = useVueTable({
  get data() {
    return allAzureUsers.value;
  },
  get pageCount() {
    return Math.ceil(total.value / defaultParams.value.pageSize);
  },
  initialState: {
    columnPinning: {
      right: ["actions"],
    },
  },
  columns: columns,
  getCoreRowModel: getCoreRowModel(),
  getPaginationRowModel: getPaginationRowModel(),
  getFilteredRowModel: getFilteredRowModel(),
  getSortedRowModel: getSortedRowModel(),

  onPaginationChange: (updater) => {
    updater instanceof Function
      ? (paginationState.value = updater(paginationState.value))
      : (paginationState.value = updater);
  },
  onSortingChange: (updater) => {
    updater instanceof Function
      ? (sortingState.value = updater(sortingState.value))
      : (sortingState.value = updater);
  },
  state: {
    get pagination() {
      return paginationState.value;
    },
    get sorting() {
      return sortingState.value;
    },
    get columnVisibility() {
      return columnVisibility.value;
    },
  },
  manualPagination: true,
});

const debounceSearch = useDebounceFn((phrase: string) => {
  filterState.value = phrase;
}, 500);
const rowsIndexPointer = useRowsIndexPointer(table);

watch(
  paramsState,
  () => {
    fetchUsers();

    handleParamsChange({
      pageSize: paramsState.results,
      sortDir: paramsState.sortDir,
      sortKey: paramsState.sortKey,
      filter: paramsState.filter,
    });
  },
  { immediate: true, deep: true },
);

const skeletonArray = useSkeletonArray(table, 10);
const { columnsToggleList, toggleColumnVisibility } = useColumnVisibility(
  table,
  columnVisibility,
);
</script>
